import { defineStore } from 'pinia'
import { useDerivativeMarketStore } from './market'
import { useDerivativePricesStore } from './prices'
import { useDerivativeMarketDataStore } from './marketData'
import { cacheRwaPriceFeedService } from '@shared/service/rwa'
import {
  createRequestKey,
  CLIENT_REQUEST_KEYS,
  apiCallsRequestCache
} from '@/app/services/apiCallsRequestCache'
import { MarketCategoryType } from '@/types/enums'
import type { TradeExecutionSide } from '@injectivelabs/sdk-ts/types/light'
import type {
  DerivativeTrade,
  TcDerivativePosition
} from '@injectivelabs/sdk-ts'
import type {
  BffDerivativeMarket,
  SharedUiOrderbookWithSequence
} from '@shared/types'
import type { MarketMarkPriceMap, MarketIndexPriceMap } from '@/types/trade'

const importDerivativeMessage = () => import('./message')
const importDerivativeStreamBroker = () => import('./streamBroker')
const importLiveMarketPricePollingBroker = () =>
  import('@/app/services/liveMarketPricePollingBroker')

type DerivativeStoreState = Record<string, never>

type FetchPositionMarketsPricesOptions = {
  marketIds?: string[]
}

const initialStateFactory = (): DerivativeStoreState => ({})

export const useDerivativeStore = defineStore('derivative', {
  state: (): DerivativeStoreState => initialStateFactory(),
  getters: {
    buys: () => {
      return useDerivativeMarketDataStore().buys
    },

    sells: () => {
      return useDerivativeMarketDataStore().sells
    },

    marketIdsFromQuery: () => {
      return useDerivativeMarketStore().marketIdsFromQuery
    },

    marketByIdOrSlug: () => {
      return (marketIdOrSlug: string): undefined | BffDerivativeMarket => {
        return useDerivativeMarketStore().marketByIdOrSlug(marketIdOrSlug)
      }
    },

    perpetualMarkets: () => {
      return useDerivativeMarketStore().perpetualMarkets
    },

    expiryFuturesMarkets: () => {
      return useDerivativeMarketStore().expiryFuturesMarkets
    },

    activeMarketIds: () => {
      return useDerivativeMarketStore().activeMarketIds
    },

    tradableDenoms: () => {
      return useDerivativeMarketStore().tradableDenoms
    },

    unverifiedDenoms: () => {
      return useDerivativeMarketStore().unverifiedDenoms
    },

    marketsWithSummary: () => {
      return useDerivativeMarketStore().marketsWithSummary
    },

    marketMarkPriceMap: () => {
      return useDerivativePricesStore().marketMarkPriceMap
    },

    marketIndexPriceMap: () => {
      return useDerivativePricesStore().marketIndexPriceMap
    },

    marketMarkDisplayPriceMap: () => {
      return useDerivativePricesStore().marketMarkDisplayPriceMap
    },

    marketIndexDisplayPriceMap: () => {
      return useDerivativePricesStore().marketIndexDisplayPriceMap
    },

    trades: () => {
      return useDerivativeMarketDataStore().trades
    },

    orderbook: () => {
      return useDerivativeMarketDataStore().orderbook
    }
  },
  actions: {
    setMarketIndexPrice(
      marketId: string,
      marketIndexPrice: MarketIndexPriceMap[string]
    ) {
      useDerivativePricesStore().setMarketIndexPrice(marketId, marketIndexPrice)
    },

    setMarketIndexPriceMap(
      marketIndexPriceMap: MarketIndexPriceMap,
      markets?: BffDerivativeMarket[]
    ) {
      useDerivativePricesStore().setMarketIndexPriceMap(
        marketIndexPriceMap,
        markets
      )
    },

    mergeMarketIndexPriceMap(
      marketIndexPriceMap: MarketIndexPriceMap,
      markets?: BffDerivativeMarket[]
    ) {
      useDerivativePricesStore().mergeMarketIndexPriceMap(
        marketIndexPriceMap,
        markets
      )
    },

    setMarketMarkPrice(
      marketId: string,
      marketMarkPrice: MarketMarkPriceMap[string]
    ) {
      useDerivativePricesStore().setMarketMarkPrice(marketId, marketMarkPrice)
    },

    setMarketMarkPriceMap(
      marketMarkPriceMap: MarketMarkPriceMap,
      markets?: BffDerivativeMarket[]
    ) {
      useDerivativePricesStore().setMarketMarkPriceMap(
        marketMarkPriceMap,
        markets
      )
    },

    mergeMarketMarkPriceMap(
      marketMarkPriceMap: MarketMarkPriceMap,
      markets?: BffDerivativeMarket[]
    ) {
      useDerivativePricesStore().mergeMarketMarkPriceMap(
        marketMarkPriceMap,
        markets
      )
    },

    async fetchMarketsIndexPrices({
      markets,
      marketIds
    }: {
      marketIds: string[]
      markets?: BffDerivativeMarket[]
    }) {
      await useDerivativePricesStore().fetchMarketsIndexPrices({
        markets,
        marketIds
      })
    },

    async fetchMarketsMarkPrices({
      markets
    }: {
      markets: BffDerivativeMarket[]
    }) {
      await useDerivativePricesStore().fetchMarketsMarkPrices({ markets })
    },

    async fetchPositionMarketsPrices({
      marketIds
    }: FetchPositionMarketsPricesOptions = {}) {
      const positionStore = usePositionStore()
      const derivativeStore = useDerivativeStore()
      const positionMarketIds = uniqueMarketIds(
        marketIds ||
          positionStore.positions.map((position) => position.marketId)
      )

      if (positionMarketIds.length === 0) {
        return
      }

      const positionMarkets = positionMarketIds
        .map((marketId) => derivativeStore.marketByIdOrSlug(marketId))
        .filter((market): market is BffDerivativeMarket => !!market)

      await apiCallsRequestCache.dedupe(
        createRequestKey(CLIENT_REQUEST_KEYS.positionMarketPrices, {
          marketIds: positionMarketIds
        }),
        async () => {
          await derivativeStore.fetchMarketsIndexPrices({
            markets: positionMarkets,
            marketIds: positionMarketIds
          })

          const marketsMissingIndexPrices = positionMarkets.filter((market) => {
            return !derivativeStore.marketIndexPriceMap[market.marketId]?.price
          })

          if (marketsMissingIndexPrices.length === 0) {
            return
          }

          await derivativeStore.fetchMarketsMarkPrices({
            markets: marketsMissingIndexPrices
          })
        }
      )
    },

    setOrderbook(orderbook?: SharedUiOrderbookWithSequence) {
      useDerivativeMarketDataStore().setOrderbook(orderbook)
    },

    setTrades(trades: DerivativeTrade[]) {
      useDerivativeMarketDataStore().setTrades(trades)
    },

    submitDerivativeChase: lazyPiniaAction(
      importDerivativeMessage,
      'submitDerivativeChase'
    ),
    submitDerivativeLimitOrder: lazyPiniaAction(
      importDerivativeMessage,
      'submitDerivativeLimitOrder'
    ),
    submitDerivativeStopLimitOrder: lazyPiniaAction(
      importDerivativeMessage,
      'submitDerivativeStopLimitOrder'
    ),
    cancelDerivativeOrder: lazyPiniaAction(
      importDerivativeMessage,
      'cancelDerivativeOrder'
    ),
    submitDerivativeStopMarketOrder: lazyPiniaAction(
      importDerivativeMessage,
      'submitDerivativeStopMarketOrder'
    ),
    batchCancelDerivativeOrder: lazyPiniaAction(
      importDerivativeMessage,
      'batchCancelDerivativeOrder'
    ),

    streamMarketsMarkPrices: lazyPiniaAction(
      importDerivativeStreamBroker,
      'streamMarketsMarkPrices'
    ),
    cancelMarketsMarkPrices: lazyPiniaAction(
      importDerivativeStreamBroker,
      'cancelMarketsMarkPrices'
    ),
    streamMarketsIndexPrices: lazyPiniaAction(
      importDerivativeStreamBroker,
      'streamMarketsIndexPrices'
    ),
    cancelMarketsIndexPrices: lazyPiniaAction(
      importDerivativeStreamBroker,
      'cancelMarketsIndexPrices'
    ),
    getMarketStreamBrokerActiveSubscriptions: lazyPiniaAction(
      importDerivativeStreamBroker,
      'getMarketStreamBrokerActiveSubscriptions'
    ),
    pollLiveMarketPrices: lazyPiniaAction(
      importLiveMarketPricePollingBroker,
      'pollLiveMarketPrices'
    ),
    cancelLiveMarketPricePolling: lazyPiniaAction(
      importLiveMarketPricePollingBroker,
      'cancelLiveMarketPricePolling'
    ),
    getLiveMarketPricePollingSubscriptions: lazyPiniaAction(
      importLiveMarketPricePollingBroker,
      'getLiveMarketPricePollingSubscriptions'
    ),

    async appendMarketId(marketIdFromQuery: string) {
      useDerivativeMarketStore().appendMarketId(marketIdFromQuery)
    },

    async getMarketMarkPrice(market: BffDerivativeMarket) {
      await useDerivativePricesStore().getMarketMarkPrice(market)
    },

    async fetchOrderbook(marketId: string) {
      await useDerivativeMarketDataStore().fetchOrderbook(marketId)
    },

    async fetchTrades({
      marketId,
      executionSide
    }: {
      marketId: string
      executionSide?: TradeExecutionSide
    }) {
      await useDerivativeMarketDataStore().fetchTrades({
        marketId,
        executionSide
      })
    },

    async fetchRWAMarketIsOpen(market: BffDerivativeMarket) {
      const isSedaRwaMarket = market.categories?.includes(
        MarketCategoryType.SEDA
      )
      const isPythRwaMarket = market.categories?.includes(
        MarketCategoryType.RWA
      )

      if ((!isPythRwaMarket && !isSedaRwaMarket) || !market.oracleBase) {
        return true
      }

      if (isSedaRwaMarket) {
        return await cacheRwaPriceFeedService.fetchSedaRwaMarketOpenNoThrow(
          market.oracleBase.split('/')?.[0] || market.marketId
        )
      }

      return await cacheRwaPriceFeedService.fetchPythRwaMarketOpenNoThrow(
        market.oracleBase
      )
    },

    async fetchPythRWAMarketIsOpen(oracle: string) {
      return await cacheRwaPriceFeedService.fetchPythRwaMarketOpenNoThrow(
        oracle
      )
    },

    async fetchSedaRwaMarketIsOpen(oracle: string) {
      return await cacheRwaPriceFeedService.fetchSedaRwaMarketOpenNoThrow(
        oracle
      )
    },

    updateMarkPriceMapFromPosition(positions: TcDerivativePosition[]) {
      useDerivativePricesStore().updateMarkPriceMapFromPosition(positions)
    },

    resetOrderbookAndTrades() {
      useDerivativeMarketDataStore().resetOrderbookAndTrades()
    },

    reset() {
      useDerivativeMarketDataStore().reset()
      useDerivativePricesStore().reset()
    }
  }
})

function uniqueMarketIds(marketIds: string[]) {
  return [...new Set(marketIds.filter(Boolean))].sort()
}
